import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { LocationTwoIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getJourneyPlace } from '../../api/journey-place/get-journey-place'
import { updateJourneyPlace } from '../../api/journey-place/update-journey-place'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import JourneyPlaceForm from '../../components/pages/journey-places/journey-place-form'
import { mapJourneyPlaceToInput } from '../../components/pages/journey-places/utils'
import { Paths } from '../../lib/constants/path'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

const JourneyPlaceUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()

  if (!id || isNaN(parseInt(id)))
    return <ItemNotFound title="Identifiant de lieu de séjour invalide" />

  const { data: journeyPlace, isPending } = useQuery({
    queryKey: ['journey-place', id],
    queryFn: () => getJourneyPlace(+id)
  })

  const { mutateAsync } = useMutation({
    mutationFn: updateJourneyPlace,
    onError: showAxiosError,
    onSuccess() {
      toast.success('Lieu de séjour modifié avec succès')
      queryClient.invalidateQueries({ exact: true, queryKey: ['journey-place', id] })
      queryClient.removeQueries({
        queryKey: ['journey-place', Number(id), 'oto-individual-places']
      })
    }
  })

  if (isPending) return <LoadingScreen />
  if (!journeyPlace) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Modifier un lieu de séjour'} icon={LocationTwoIcon} />
      <div className={'h-4'} />

      <Box>
        <JourneyPlaceForm
          defaultValues={mapJourneyPlaceToInput(journeyPlace)}
          journeyPlaceId={journeyPlace.id}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.JOURNEY_PLACE_LIST_PAGE),
            onUpdate: (values) => mutateAsync({ id: journeyPlace.id, ...values }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: journeyPlace.id, ...values })
              navigate(Paths.JOURNEY_PLACE_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: journeyPlace.id, ...values })
              back(Paths.JOURNEY_PLACE_LIST_PAGE)
            }
          })}
        />
      </Box>
    </div>
  )
}

export default JourneyPlaceUpdatePage
